import { FeedItem } from "./FeedItem";
import { ScrollArea } from "../components/ui/scroll-area";
import { Loader2, Moon, Sun } from "lucide-react";
import { useNow } from "../hooks/useNow";

export function NewsFeed({
  articles,
  activeArticle,
  newArticleIds,
  onSelectArticle,
  loading,
  lastUpdated,
  theme,
  onToggleTheme,
}) {
  const now = useNow();
  const isDark = theme === "dark";
  const ThemeIcon = isDark ? Sun : Moon;
  if (loading && articles.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8" data-testid="feed-loading">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin mb-3" />
        <p className="text-xs tracking-[0.2em] uppercase font-mono text-gray-400">
          Henter nyheder...
        </p>
        <p className="text-[10px] font-mono text-gray-300 dark:text-gray-400 mt-1">
          Indlæser de seneste artikler
        </p>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8" data-testid="feed-empty">
        <p className="text-xs tracking-[0.2em] uppercase font-mono text-gray-400">
          Ingen nyheder endnu
        </p>
        <p className="text-[10px] font-mono text-gray-300 dark:text-gray-400 mt-1">
          Nye artikler tjekkes ca. hvert 5. minut
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="divide-y divide-[#E5E5E5] dark:divide-zinc-800" data-testid="news-feed">
        <div className="px-5 py-3 border-b border-[#E5E5E5] dark:border-zinc-800 bg-white dark:bg-zinc-950 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400">
                NYHEDSFEED
              </span>
              <button
                type="button"
                onClick={onToggleTheme}
                className="p-1 rounded-sm text-gray-400 hover:text-[#0A0A0A] dark:hover:text-zinc-50 transition-colors"
                aria-label={isDark ? "Skift til lyst tema" : "Skift til mørkt tema"}
                title={isDark ? "Lyst tema" : "Mørkt tema"}
                data-testid="theme-toggle"
              >
                <ThemeIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
              </button>
            </div>
            {lastUpdated && (
              <span className="text-[10px] font-mono text-gray-400">
                Opdateret {new Date(lastUpdated).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>

        {articles.map((article) => (
          <FeedItem
            key={article.id}
            article={article}
            isActive={activeArticle?.id === article.id}
            isNew={newArticleIds.has(article.id)}
            now={now}
            onClick={onSelectArticle}
          />
        ))}
      </div>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {newArticleIds.size > 0 ? `${newArticleIds.size} nye artikler tilgængelige` : ""}
      </div>
    </ScrollArea>
  );
}
